import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { Product } from '../data/products'

export interface CartItem {
  id: string
  productId: string
  product: Product
  color: string
  size: string
  quantity: number
}

interface CartState {
  items: CartItem[]
  isOpen: boolean
  addItem: (product: Product, color: string, size: string) => void
  removeItem: (id: string) => void
  updateQuantity: (id: string, quantity: number) => void
  toggleCart: () => void
  openCart: () => void
  closeCart: () => void
  clearCart: () => void
  totalItems: () => number
  totalPrice: () => number
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      isOpen: false,

      addItem: (product, color, size) => {
        set((state) => {
          const existingItemIndex = state.items.findIndex(
            (item) =>
              item.productId === product.id &&
              item.color === color &&
              item.size === size,
          )

          if (existingItemIndex >= 0) {
            const newItems = [...state.items]
            newItems[existingItemIndex].quantity += 1
            return { items: newItems }
          }

          const newItem: CartItem = {
            id: `${product.id}-${color}-${size}-${Date.now()}`,
            productId: product.id,
            product,
            color,
            size,
            quantity: 1,
          }

          return { items: [...state.items, newItem] }
        })
        get().openCart()
      },

      removeItem: (id) => {
        set((state) => ({
          items: state.items.filter((item) => item.id !== id),
        }))
      },

      updateQuantity: (id, quantity) => {
        set((state) => ({
          items: state.items.map((item) =>
            item.id === id
              ? { ...item, quantity: Math.max(1, quantity) }
              : item,
          ),
        }))
      },

      toggleCart: () => set((state) => ({ isOpen: !state.isOpen })),
      openCart: () => set({ isOpen: true }),
      closeCart: () => set({ isOpen: false }),
      clearCart: () => set({ items: [] }),

      totalItems: () => {
        return get().items.reduce((total, item) => total + item.quantity, 0)
      },

      totalPrice: () => {
        return get().items.reduce(
          (total, item) => total + item.product.price * item.quantity,
          0,
        )
      },
    }),
    {
      name: 'vsc-cart-storage',
      partialize: (state) => ({ items: state.items }), // Only persist items
    },
  ),
)
